import { supabase } from "./supabase";
import { normalizeBrandName } from "./brand-data";

export type BrandSuggestionStatus = "pending" | "approved" | "rejected";

export type ManagedBrandSuggestion = {
  id: string;
  name: string;
  status: BrandSuggestionStatus;
  requestCount: number;
  imageUrl: string;
  storagePath: string;
  createdAt: string;
  updatedAt: string;
  approvedAt: string | null;
};

export type PublicNewBrand = {
  name: string;
  imageUrl: string;
  addedAt: string;
};

type ManagedBrandSuggestionRow = {
  id: string;
  suggested_name: string;
  status: BrandSuggestionStatus;
  request_count: number;
  image_url: string;
  storage_path: string;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
};

function normalizeManagedSuggestion(row: ManagedBrandSuggestionRow): ManagedBrandSuggestion {
  return {
    id: row.id,
    name: row.suggested_name,
    status: row.status,
    requestCount: Number(row.request_count),
    imageUrl: row.image_url ?? "",
    storagePath: row.storage_path ?? "",
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    approvedAt: row.approved_at,
  };
}

export async function submitBrandSuggestion(name: string): Promise<void> {
  const normalized = normalizeBrandName(name);
  if (normalized.length < 2) throw new Error("Enter at least two characters for the brand name.");
  const { error } = await supabase.rpc("submit_brand_suggestion", { p_name: normalized });
  if (error) throw error;
}

export async function loadManagedBrandSuggestions(): Promise<ManagedBrandSuggestion[]> {
  const { data, error } = await supabase.rpc("admin_list_brand_suggestions");
  if (error) throw error;
  return ((data ?? []) as ManagedBrandSuggestionRow[]).map(normalizeManagedSuggestion);
}

export async function uploadSuggestedBrandImage(suggestion: ManagedBrandSuggestion, file: File) {
  if (!file.type.startsWith("image/")) throw new Error("Choose an image file.");
  if (file.size > 4 * 1024 * 1024) throw new Error("Brand images must be 4 MB or smaller.");
  const extension = file.name.split(".").pop()?.toLocaleLowerCase().replace(/[^a-z0-9]/g, "") || "png";
  const storagePath = `suggestions/${suggestion.id}-${crypto.randomUUID()}.${extension}`;
  const uploaded = await supabase.storage.from("brand-images").upload(storagePath, file, { contentType: file.type, upsert: false });
  if (uploaded.error) throw uploaded.error;
  return {
    imageUrl: supabase.storage.from("brand-images").getPublicUrl(storagePath).data.publicUrl,
    storagePath,
  };
}

export async function saveManagedBrandSuggestion(
  suggestion: ManagedBrandSuggestion,
  patch: { name: string; imageUrl: string; storagePath: string; status: BrandSuggestionStatus },
): Promise<ManagedBrandSuggestion> {
  const { data, error } = await supabase.rpc("admin_update_brand_suggestion", {
    p_id: suggestion.id,
    p_name: normalizeBrandName(patch.name),
    p_image_url: patch.imageUrl,
    p_storage_path: patch.storagePath,
    p_status: patch.status,
  });
  if (error) throw error;
  const result = data as Record<string, unknown> | null;
  return {
    ...suggestion,
    name: String(result?.name ?? patch.name),
    status: (result?.status ?? patch.status) as BrandSuggestionStatus,
    requestCount: Number(result?.requestCount ?? suggestion.requestCount),
    imageUrl: String(result?.imageUrl ?? patch.imageUrl),
    storagePath: String(result?.storagePath ?? patch.storagePath),
    approvedAt: result?.approvedAt ? String(result.approvedAt) : suggestion.approvedAt,
    updatedAt: new Date().toISOString(),
  };
}

export async function removeSuggestedBrandImage(storagePath: string) {
  if (!storagePath) return;
  await supabase.storage.from("brand-images").remove([storagePath]);
}

export async function loadPublicNewBrands(): Promise<PublicNewBrand[]> {
  const { data, error } = await supabase.rpc("get_public_new_brands");
  if (error) throw error;
  return ((data ?? []) as Array<{ brand_name: string; image_url: string; added_at: string }>).map((row) => ({
    name: row.brand_name,
    imageUrl: row.image_url,
    addedAt: row.added_at,
  }));
}
